import { useState, useCallback } from 'react';
import { FileText, Loader } from 'lucide-react';
import { triggerDownload } from '@/utils/download';
import { ViewerShell } from './ViewerShell';
import type { FavoriteCachedData } from '@/types/favorites';

interface DocumentViewerProps {
  url: string;
  title?: string;
  fileName?: string;
  onClose: () => void;
  onDownload?: () => void;
  fileId?: string;
  favoriteCachedData?: FavoriteCachedData;
  conversionStatus?: 'processing' | 'completed' | 'failed';
}

export function DocumentViewer({
  url,
  title,
  fileName,
  onClose,
  onDownload,
  fileId,
  favoriteCachedData,
  conversionStatus,
}: DocumentViewerProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;

  const handleDownload = useCallback(() => {
    if (onDownload) {
      onDownload();
    } else {
      triggerDownload(url, fileName || title || 'document');
    }
  }, [url, fileName, title, onDownload]);

  return (
    <ViewerShell
      title={title || 'Document'}
      type="doc"
      fileId={fileId}
      favoriteCachedData={favoriteCachedData}
      onClose={onClose}
      onDownload={handleDownload}
      keyboardHint="Esc to close"
    >
      <div className="flex flex-col w-full h-full min-h-full">
        {conversionStatus === 'processing' && (
          <div className="flex-1 flex items-center justify-center bg-[#faf8f5]">
            <div className="text-center max-w-sm p-6">
              <Loader className="animate-spin text-[#8e4585] mx-auto" size={32} />
              <h3 className="text-base font-medium text-[#5a3d58] mt-4 mb-2">
                Converting to PDF...
              </h3>
              <p className="text-[#8e4585]/60 text-sm">
                This document is being converted to PDF for better viewing. It will be ready
                shortly.
              </p>
            </div>
          </div>
        )}

        {conversionStatus !== 'processing' && loading && !error && (
          <div className="flex-1 flex items-center justify-center bg-[#faf8f5]">
            <div className="text-center">
              <Loader className="animate-spin text-[#8e4585] mx-auto" size={28} />
              <p className="text-[#5a3d58] mt-3 text-sm">Loading document...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex-1 flex items-center justify-center bg-[#faf8f5]">
            <div className="text-center max-w-md p-6">
              <FileText size={48} className="text-[#e5bce5] mx-auto mb-4" />
              <h3 className="text-base font-medium text-[#5a3d58] mb-2">
                Cannot preview this document
              </h3>
              <p className="text-[#8e4585]/60 text-sm mb-4">
                This document type may not be supported, or the file is too large. Use the download
                button in the header to save the file.
              </p>
            </div>
          </div>
        )}

        {!error && conversionStatus !== 'processing' && (
          <iframe
            src={viewerUrl}
            className={`flex-1 w-full border-0 bg-white ${loading ? 'h-0 overflow-hidden' : ''}`}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setError(true);
            }}
            title={title || 'Document Preview'}
          />
        )}
      </div>
    </ViewerShell>
  );
}
